import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Platform, Text, View } from 'react-native'
import { QueryClient, QueryClientProvider, useIsFetching, useQueryClient } from '@tanstack/react-query'
import MockDate from 'mockdate'
import { VisualDiagnosticsContext } from '../../lib/visualDiagnostics'
import { AuthContext } from '../../hooks/useAuth'
import { visualResponse, visualUser, VISUAL_NOW, type VisualCase } from './transport'
import { createInteractiveGame, type InteractiveGameSnapshot } from './interactiveGame'
import { reportError } from '../../lib/reporting'

function readInteractiveSession(id: string): InteractiveGameSnapshot | undefined {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return
  try {
    const stored = window.sessionStorage.getItem(`turn-concept-session:${id}`)
    if (!stored) return
    const snapshot = JSON.parse(stored) as InteractiveGameSnapshot
    if (snapshot.game?.id !== 'visual-game' || !snapshot.game.state || !Array.isArray(snapshot.messages) || !Number.isInteger(snapshot.version)) throw new Error('Invalid concept session')
    return snapshot
  } catch (error) { reportError(error, { tags: { surface: 'turn-concept-gallery', action: 'restore-session' } }) }
}

function saveInteractiveSession(id: string, snapshot: InteractiveGameSnapshot) {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return
  try { window.sessionStorage.setItem(`turn-concept-session:${id}`, JSON.stringify(snapshot)) }
  catch (error) { reportError(error, { tags: { surface: 'turn-concept-gallery', action: 'save-session' } }) }
}

/** Only mounted by the gated visual route. Real screens retain their hooks;
 * transport and clock are scoped to the fixture, restored when it unmounts. */
export function FixtureProviders({ scenario, children, interactiveSession, readinessId }: { scenario: VisualCase; children: React.ReactNode; interactiveSession?: string; readinessId?: string }) {
  const [ready, setReady] = useState(false)
  const [unhandled, setUnhandled] = useState<string[]>([])
  const interactive = useRef<ReturnType<typeof createInteractiveGame> | null>(null)
  const [client] = useState(() => new QueryClient({ defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false }, mutations: { retry: false },
  } }))
  useLayoutEffect(() => {
    const originalFetch = globalThis.fetch
    const OriginalWebSocket = globalThis.WebSocket
    MockDate.set(scenario.now ?? VISUAL_NOW)
    interactive.current = interactiveSession
      ? createInteractiveGame(scenario, readInteractiveSession(interactiveSession), snapshot => saveInteractiveSession(interactiveSession, snapshot))
      : null
    globalThis.fetch = (input, init) => {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
      const index = url.indexOf('/api/v1/')
      if (index < 0) return originalFetch(input, init)
      const pathname = url.slice(index)
      const method = init?.method ?? 'GET'
      const response = interactive.current
        ? interactive.current.respond(pathname, method, typeof init?.body === 'string' ? init.body : undefined)
        : visualResponse(scenario, pathname, method)
      if (!response) {
        const key = `${method} ${pathname.split('?')[0]}`
        setUnhandled(current => current.includes(key) ? current : [...current, key])
      }
      return Promise.resolve(new Response(JSON.stringify(response?.body ?? { error: 'Unseeded visual fixture request' }), {
        status: response ? (response.status ?? 200) : 501, headers: { 'Content-Type': 'application/json' },
      }))
    }
    class FixtureWebSocket {
      static CONNECTING = 0; static OPEN = 1; static CLOSING = 2; static CLOSED = 3
      readyState = 0
      onopen: ((event: Event) => void) | null = null
      onmessage: ((event: MessageEvent) => void) | null = null
      onclose: ((event: CloseEvent) => void) | null = null
      onerror: ((event: Event) => void) | null = null
      constructor(url: string, protocols?: string | string[]) {
        // Metro/HMR connections use the real transport.
        if (!url.includes('/api/v1/')) return new OriginalWebSocket(url, protocols) as unknown as FixtureWebSocket
        setTimeout(() => { this.readyState = 1; this.onopen?.({} as Event) }, 0)
      }
      send(data: string) {
        if (JSON.parse(data).type === 'PING') this.onmessage?.({ data: JSON.stringify({ type: 'PONG' }) } as MessageEvent)
      }
      close() { this.readyState = 3 }
      addEventListener() {}
      removeEventListener() {}
    }
    globalThis.WebSocket = FixtureWebSocket as unknown as typeof WebSocket
    setReady(true)
    return () => {
      globalThis.fetch = originalFetch
      globalThis.WebSocket = OriginalWebSocket
      MockDate.reset()
      client.clear()
    }
  }, [client, scenario, interactiveSession])
  const auth = {
    user: interactive.current?.user ?? (scenario.data === 'vacation-active' ? { ...visualUser, vacation: { startedAt: '2026-10-04T12:00:00Z', endsAt: '2026-10-07T12:00:00Z', message: 'Gone fishing. Guard the river.' } } : visualUser), isAuthenticated: true, isExplicitLogout: false, isLoading: false,
    login: () => Promise.resolve(), logout: () => Promise.resolve(), refreshUser: () => Promise.resolve(), setSession: () => Promise.resolve(),
    register: () => Promise.resolve({ message: 'Check your email to finish signing up.', email: visualUser.email }),
  }
  if (!ready) return null
  return (
    <AuthContext.Provider value={auth}>
      <QueryClientProvider client={client}>
        <FixtureDiagnostics caseId={scenario.id} readinessId={readinessId} unhandled={unhandled} allowQueryErrors={scenario.data === 'error' || scenario.data === 'invalid-code' || /confirm-email|reset-password/.test(scenario.source)}>{children}</FixtureDiagnostics>
      </QueryClientProvider>
    </AuthContext.Provider>
  )
}

function FixtureDiagnostics({ caseId, readinessId, unhandled, allowQueryErrors, children }: { caseId: string; readinessId?: string; unhandled: string[]; allowQueryErrors: boolean; children: React.ReactNode }) {
  const client = useQueryClient()
  const failedQueries = client.getQueryCache().getAll().filter(query => query.state.status === 'error')
  const fetching = useIsFetching()
  const readinessKey = readinessId ?? caseId
  const [settledFor, setSettledFor] = useState<string | null>(null)
  useEffect(() => {
    setSettledFor(null)
    if (fetching > 0) return
    // Native message threads scroll after layout; allow that work to finish
    // before advertising the screenshot checkpoint, even on a busy emulator.
    const timer = setTimeout(() => setSettledFor(readinessKey), 1500)
    return () => clearTimeout(timer)
  }, [fetching, readinessKey])
  const settled = fetching === 0 && settledFor === readinessKey
  const diagnostics = (
    <>
      {settled && <View testID={`visual-ready-${caseId}`} collapsable={false} style={{ position: 'absolute', bottom: 0, left: 0, width: 1, height: 1 }} />}
      {settled && readinessId && <View testID={readinessId} collapsable={false} style={{ position: 'absolute', bottom: 0, left: 0, width: 1, height: 1 }} />}
      {!allowQueryErrors && failedQueries.length > 0 && <View testID="visual-runtime-error" style={{ position: 'absolute', top: 80, left: 0, right: 0, backgroundColor: '#ff0000' }}><Text>{failedQueries.map(query => String(query.state.error)).join('\n')}</Text></View>}
      {unhandled.length > 0 && <View testID="visual-unseeded-request" style={{ position: 'absolute', top: 100, left: 0, right: 0, backgroundColor: '#ff0000' }}><Text>{unhandled.join('\n')}</Text></View>}
    </>
  )
  return <VisualDiagnosticsContext.Provider value={diagnostics}>{children}{diagnostics}</VisualDiagnosticsContext.Provider>
}
